// Auth context: tracks the signed-in Firebase user and whether they are an
// admin (users/{uid}/role === "admin"). Only admins are treated as authorized.
import React, { createContext, useContext, useEffect, useState } from 'react';
import { onAuthStateChanged, signOut as fbSignOut, User } from 'firebase/auth';
import { ref, get } from 'firebase/database';
import { auth, db } from './firebase';

interface AuthState {
  user: User | null;
  isAdmin: boolean;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState>({
  user: null,
  isAdmin: false,
  loading: true,
  signOut: async () => {},
});

export const useAuth = () => useContext(AuthContext);

// Returns true when users/{uid}/role === "admin".
export async function checkIsAdmin(uid: string): Promise<boolean> {
  const snap = await get(ref(db, `users/${uid}/role`));
  return snap.exists() && snap.val() === 'admin';
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (u) {
        try {
          const admin = await checkIsAdmin(u.uid);
          setUser(u);
          setIsAdmin(admin);
        } catch {
          setUser(u);
          setIsAdmin(false);
        }
      } else {
        setUser(null);
        setIsAdmin(false);
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  const signOut = async () => {
    await fbSignOut(auth);
  };

  return (
    <AuthContext.Provider value={{ user, isAdmin, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};
